import { useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const SERVICES = [
  "Digital Acquisition Funnels & SEO",
  "AI Automation & Salesforce CRM",
  "Commercial Market Assessment",
  "Not sure yet — advise me",
];

const EMPTY = { name: "", email: "", company: "", service: SERVICES[0], message: "" };

export default function LeadForm({ prefill = null }) {
  const [form, setForm] = useState(() => {
    if (!prefill) return EMPTY;
    const service = SERVICES.includes(prefill.service) ? prefill.service : SERVICES[3];
    return { ...EMPTY, ...prefill, service };
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Tell us your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";
    if (form.message.trim().length < 10) errs.message = "A few words about your goal (min. 10 characters)";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry received — we reply within one business day.", {
        description: "Your message is safely with the Vector team.",
      });
      setForm(EMPTY);
      setErrors({});
    } catch (err) {
      toast.error("Something went wrong.", {
        description: "Please try again, or email us directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-600 focus:border-neon/60 focus:outline-none focus:ring-1 focus:ring-neon/40 transition-colors";

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Name *</label>
          <Input
            id="cf-name"
            value={form.name}
            onChange={set("name")}
            placeholder="Jane Founder"
            className={field}
            data-testid="contact-input-name"
          />
          {errors.name && <p className="mt-2 text-xs text-red-600" data-testid="contact-error-name">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Work email *</label>
          <Input
            id="cf-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="jane@company.eu"
            className={field}
            data-testid="contact-input-email"
          />
          {errors.email && <p className="mt-2 text-xs text-red-600" data-testid="contact-error-email">{errors.email}</p>}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Company</label>
          <Input
            id="cf-company"
            value={form.company}
            onChange={set("company")}
            placeholder="Company BV"
            className={field}
            data-testid="contact-input-company"
          />
        </div>
        <div>
          <label htmlFor="cf-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">I'm interested in</label>
          <select
            id="cf-service"
            value={form.service}
            onChange={set("service")}
            className={`${field} appearance-none`}
            data-testid="contact-select-service"
          >
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Your growth goal *</label>
        <Textarea
          id="cf-message"
          value={form.message}
          onChange={set("message")}
          rows={5}
          placeholder="Where is your pipeline leaking, and what would 30% more pipeline mean for you?"
          className={`${field} resize-none`}
          data-testid="contact-input-message"
        />
        {errors.message && <p className="mt-2 text-xs text-red-600" data-testid="contact-error-message">{errors.message}</p>}
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="contact-form-submit-button"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-neon px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(47,168,224,0.4)] disabled:opacity-60 sm:w-auto"
      >
        {loading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Transmitting…</>
        ) : (
          <>Send enquiry <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
        )}
      </Button>
    </form>
  );
}
